import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Space,
  Alert,
  Skeleton,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";

import { RecurrenceMode } from "../../flatbuffers/generated/archive/recurrence-mode";
import { TimedActionDraft, ControlOption, ScheduledActionSelectors } from "./scheduledActionTypes";
import {
  daysOfWeekToBooleans,
  booleansToDaysOfWeek,
  daysOfMonthToDays,
  daysToMonthMask,
  hasLastDay,
  withLastDay,
} from "./scheduledActionBitmask";
import {
  secondsOfDayToMoment,
  momentToSecondsOfDay,
  localDateTimeToISO,
  isoToLocalMoment,
  deriveStopNextDay,
} from "./scheduledActionTime";
import ScheduledActionModeFields from "./ScheduledActionModeFields";
import FloorOpeningPicker from "./FloorOpeningPicker";
import {
  parseFloorSpec,
  describeFloors,
  floorsToSpec,
  type FloorSpecError,
  type FloorSpecErrorCode,
} from "./floorSpec";
import {
  controlRequiresFloor,
  GENERIC_OUTPUT_FIRST,
  GENERIC_OUTPUT_LAST,
  isFloorBasedControl,
  isValidOutputNumber,
  takesOutputNumber,
} from "../../utils/controlFloorRules";
import { BankFloorOpening, resolveOpeningsForLifts } from "../../utils/bankFloorOpenings";

const { Option } = Select;

// ── Recurrence mode label map ────────────────────────────────────────────────

const RECURRENCE_LABELS: Record<RecurrenceMode, string> = {
  [RecurrenceMode.ONE_SHOT]: "One-shot",
  [RecurrenceMode.DAILY]: "Daily",
  [RecurrenceMode.WEEKLY]: "Weekly",
  [RecurrenceMode.MONTHLY]: "Monthly",
  [RecurrenceMode.MONTHLY_WEEKDAY]: "Monthly (weekday)",
  [RecurrenceMode.INTERVAL]: "Interval",
};

// ── Floor-spec error presentation ────────────────────────────────────────────

/**
 * Default English text per rejection code. Keyed by the stable codes the Go and
 * TypeScript validators share, so a new code fails to compile here rather than
 * silently rendering as a blank message.
 */
const FLOOR_SPEC_MESSAGES: Record<FloorSpecErrorCode, string> = {
  EMPTY_SPEC: "Enter a floor range, or leave the field empty to use the single floor.",
  EMPTY_MEMBER: "There is an empty entry — check for a stray or doubled comma.",
  UNBALANCED_BRACKET: "Brackets are unbalanced. Every interval needs an opening and a closing one.",
  NESTED_BRACKET: "Intervals cannot be nested inside one another.",
  MALFORMED_INTERVAL: "An interval needs exactly two bounds, like [1,5).",
  INVALID_INTEGER: "Floor bounds must be whole numbers.",
  BOUND_OUT_OF_RANGE: "That floor number is too large to be a real floor.",
  BOUND_ORDER: "The first bound must not be greater than the second.",
  EMPTY_INTERVAL: "This range contains no floors — [3,3) and (3,4) are both empty.",
  TOO_MANY_FLOORS: "That range covers too many floors.",
  TRAILING_CHARACTERS: "There are unexpected characters after a closing bracket.",
  FLOOR_NOT_IN_BANK: "That floor does not exist in any selected bank.",
};

/**
 * Minimal structural type for the i18next translator. Deliberately NOT
 * `ReturnType<typeof useTranslation>[0]` — that pulls in i18next's deep generic
 * key inference and trips TS2589 ("type instantiation is excessively deep").
 * Only the key + default + interpolation call shape is used here.
 */
type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

/**
 * Render a rejection for the operator: what is wrong, and where. The character
 * position matters most in a long union like "[1,5),[10,12],[20,25)", where
 * "brackets are unbalanced" alone would not say which interval to look at.
 */
function describeFloorSpecError(error: FloorSpecError, t: Translate): string {
  const message = t(
    `scheduledActions.floorSpecError.${error.code}`,
    FLOOR_SPEC_MESSAGES[error.code],
  );

  if (error.code === "FLOOR_NOT_IN_BANK") {
    return t("scheduledActions.floorSpecErrorFloor", "{{message}} (floor {{floor}})", {
      message,
      floor: error.floor,
    });
  }

  return t("scheduledActions.floorSpecErrorAt", "{{message}} (position {{offset}})", {
    message,
    offset: error.offset + 1,
  });
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface ScheduledActionFormProps {
  initial: TimedActionDraft;
  controlOptions: ControlOption[];
  selectors?: ScheduledActionSelectors;
  timezoneDefault: string;
  submitting: boolean;
  onSubmit: (draft: TimedActionDraft) => void;
  onCancel: () => void;
}

// ── Internal form values (what Ant Form stores) ──────────────────────────────

interface FormValues {
  name: string;
  control: number;
  stop_control?: number;
  // Independent active state for the start/stop command (UiCmd.active at
  // dispatch, not a distinct message) — see TimedActionDraft's own comment.
  start_active: boolean;
  stop_active: boolean;
  // Target sets. bank_ids[0] is the owning bank; lift_ids empty means bank-wide.
  bank_ids?: string[];
  lift_ids?: string[];
  /**
   * The floor set as ONE field, accepting a bare floor ("3"), an interval
   * ("[1,5)"), or any comma-separated mix of the two ("3, 7, [10,12]").
   *
   * This replaces the former separate "Floor range" and "Floor / position"
   * inputs. Two fields for one concept forced an operator to know which of them
   * won, and the answer ("the range, silently") had to be explained in help text
   * on both. The grammar already admitted a bare integer as a singleton member,
   * so one field expresses everything the two did.
   *
   * Empty means the action targets no particular floor.
   */
  floors?: string;
  /**
   * The output number (1-32) for `Control.GenericOutput`, whose `position` is
   * the controller's generic output rather than a floor. Read only for that
   * control; every other control takes its position from `floors`.
   */
  output_number?: number | null;
  enabled: boolean;
  recurrence_mode: RecurrenceMode;
  timezone?: string;
  // ONE_SHOT / INTERVAL window
  start_at?: moment.Moment | null;
  stop_at?: moment.Moment | null;
  // DAILY / WEEKLY / MONTHLY time-of-day
  start_time?: moment.Moment | null;
  stop_time?: moment.Moment | null;
  // DAILY / WEEKLY: selected day indices (0 = Sun … 6 = Sat)
  days_of_week_indices?: number[];
  // WEEKLY
  week_interval?: number;
  // MONTHLY
  days_of_month_days?: number[];
  month_last_day?: boolean;
  // MONTHLY_WEEKDAY: which occurrence (1st..5th, or "last") of the single day
  // selected in days_of_week_indices. month_week_last overrides the ordinal.
  month_week_ordinal?: number;
  month_week_last?: boolean;
  // INTERVAL: a recurring schedule is described as start + frequency + duration
  // (NOT start/stop — that one-shot vocabulary is reserved for ONE_SHOT mode).
  // duration_minutes drives the stored absolute stop_at = start_at + duration.
  interval_minutes?: number;
  duration_minutes?: number;
  // optional state filter
  valid_states?: string[];
}

// ── Helpers: draft → FormValues ──────────────────────────────────────────────

/**
 * The Floors field for a draft that carries no `floor_spec`, i.e. one described
 * by the legacy scalar `position` alone.
 *
 * Position 0 renders as EMPTY, not as the literal "0". The schema is explicit
 * that 0 is the unused value ("Target floor/position for energize-style elevator
 * commands. 0 = unused"), and `emptyTimedActionDraft` starts every action there,
 * so the overwhelming majority of rows carrying a 0 mean "no particular floor"
 * rather than "the floor whose position is 0".
 *
 * This is not cosmetic. Showing "0" made the editor claim a floor that most
 * banks do not have, which the opening grid cannot represent — so every such
 * action, new or saved, opened in the expert expression mode the grid exists to
 * keep out of the way, under an alert saying the range could not be shown.
 *
 * The trade is deliberate and one-directional: an action that really did target
 * a floor numbered 0 opens showing no floor, but saving it unchanged preserves
 * the 0 (see `resolveFloors`, which leaves the scalar alone for an empty field),
 * so nothing is lost unless the operator picks different floors on purpose.
 */
function floorsField(draft: TimedActionDraft): string {
  if (draft.position === 0) return "";

  return String(draft.position);
}

/**
 * The Floors field for any draft: its `floor_spec` when it has one, else its
 * scalar `position` through `floorsField` — with two exceptions, both because
 * the position is not what `floorsField` assumes.
 *
 * A GenericOutput draft's position is its OUTPUT NUMBER. It belongs in the
 * output field, and seeding the floor set with it would carry output 12 over as
 * floor 12 the moment the operator switched the command to a car call.
 *
 * A ShuttleFloor draft's position is a 0-based floor, so 0 is the lowest floor
 * rather than the schema's "unused" value and is shown as "0", not as empty.
 */
function floorsFieldFor(draft: TimedActionDraft): string {
  const control = draft.start_message_value;
  if (takesOutputNumber(control)) return "";
  if (draft.floor_spec != null) return draft.floor_spec;
  if (controlRequiresFloor(control)) return String(draft.position);

  return floorsField(draft);
}

/**
 * The output field for a GenericOutput draft: its stored position when that is
 * an output number VTS accepts. Empty for every other draft — a position carried
 * over from a car call is a floor, never an output to preset.
 */
function outputNumberField(draft: TimedActionDraft): number | undefined {
  if (!takesOutputNumber(draft.start_message_value)) return undefined;

  return isValidOutputNumber(draft.position) ? draft.position : undefined;
}

function draftToFormValues(draft: TimedActionDraft, timezoneDefault: string): FormValues {
  const tz = draft.timezone || timezoneDefault;
  // A brand-new action (no id) with an unset seconds-of-day starts with an EMPTY
  // time picker so the per-mode "required" validators engage. Otherwise the field
  // silently defaults to 00:00, passes validation, and (via deriveStopNextDay)
  // produces a spurious midnight→midnight 24-hour window. An existing action
  // shows its stored time even when that time is genuinely midnight.
  const timeField = (seconds: number): moment.Moment | null => {
    if (!draft.id && seconds === 0) return null;
    return secondsOfDayToMoment(seconds);
  };
  return {
    name: draft.name,
    control: draft.start_message_value,
    // 0 is Control.CarCallLock, a real command — not "no stop command". Only a
    // brand-new action may read a stored 0 as "nothing chosen yet"; a saved one
    // shows the command it holds, exactly as timeField above shows a genuine
    // midnight. Blanking it for a saved action let the prefill effect below
    // substitute the start control's complement and persist that on save.
    stop_control:
      !draft.id && draft.stop_message_value === 0 ? undefined : draft.stop_message_value,
    start_active: draft.start_message_active,
    stop_active: draft.stop_message_active,
    // The multi-selects always show a set. A draft with no explicit set is
    // rendered as its scalar predecessor, so an existing single-target action
    // opens showing exactly the bank and lift it already had.
    bank_ids: draft.bank_ids ?? (draft.bank_id !== "" ? [draft.bank_id] : []),
    lift_ids: draft.lift_ids ?? (draft.lift_id !== undefined ? [draft.lift_id] : []),
    // One field, populated from whichever of the two the row actually carries.
    // floor_spec wins when both are present, matching the server's precedence,
    // so what the operator sees is what will fire.
    //
    // Position 0 opens as an EMPTY field — see `floorsField` for why, and note
    // that unlike `timeField` above this does NOT depend on the draft having an
    // id: 0 is the schema's unused value for a floor, not a legitimate zero-hour.
    // `floorsFieldFor` names the two controls whose position is read otherwise.
    floors: floorsFieldFor(draft),
    output_number: outputNumberField(draft),
    enabled: draft.enabled,
    recurrence_mode: draft.recurrence_mode,
    timezone: tz,
    start_at:
      draft.start_at != null && draft.start_at !== "" ? isoToLocalMoment(draft.start_at, tz) : null,
    stop_at:
      draft.stop_at != null && draft.stop_at !== "" ? isoToLocalMoment(draft.stop_at, tz) : null,
    start_time: timeField(draft.start_seconds_of_day),
    stop_time: timeField(draft.stop_seconds_of_day),
    days_of_week_indices: daysOfWeekToBooleans(draft.days_of_week).reduce<number[]>(
      (acc, on, i) => (on ? [...acc, i] : acc),
      [],
    ),
    week_interval: draft.week_interval ?? 1,
    days_of_month_days: daysOfMonthToDays(draft.days_of_month ?? 0),
    month_last_day: hasLastDay(draft.days_of_month ?? 0),
    month_week_ordinal: draft.month_week_ordinal,
    month_week_last: draft.month_week_last,
    interval_minutes: draft.interval_seconds != null ? Math.round(draft.interval_seconds / 60) : 1,
    // INTERVAL duration is derived from the stored [start_at, stop_at] window
    // when editing; a new action defaults to 60 minutes.
    duration_minutes:
      draft.start_at && draft.stop_at
        ? Math.max(
            1,
            isoToLocalMoment(draft.stop_at, tz).diff(
              isoToLocalMoment(draft.start_at, tz),
              "minutes",
            ),
          )
        : 60,
    valid_states: draft.valid_states,
  };
}

// ── Helpers: FormValues → TimedActionDraft ───────────────────────────────────

/**
 * A bare, optionally negative integer and nothing else — no brackets, no commas,
 * no whitespace-separated extras.
 *
 * Used to tell "3" (which is stored as the legacy `position` scalar) apart from
 * "[3,3]" and "3,3", which resolve to the same single floor but are explicitly
 * SET notation and so are stored as a spec. Without the textual check, typing an
 * interval that happens to cover one floor would silently downgrade to a scalar.
 */
const STRICT_INTEGER_TEXT = /^-?[0-9]+$/;

/** The targeting slice of a draft: the scalars plus their set counterparts. */
type TargetingFields = Pick<
  TimedActionDraft,
  "bank_id" | "lift_id" | "bank_ids" | "lift_ids" | "floor_spec" | "position"
>;

/**
 * Split the single floors field into the draft's `position` scalar and its
 * `floor_spec` set.
 *
 * A BARE SINGLE FLOOR is written as the scalar alone with no spec, so an action
 * that targets one floor stays byte-identical to how it was stored before floor
 * sets existed — merging the two inputs must not rewrite every existing row.
 *
 * Anything resolving to more than one floor is written as the spec, AND sets
 * `position` to the lowest resolved floor. That scalar is not redundant: a VTS
 * build predating floor_spec ignores the field entirely and falls back to
 * position, so a stale edge fires at one real floor of the set rather than at
 * whatever unrelated value the row happened to carry before.
 */
function resolveFloors(
  raw: string,
  initial: TimedActionDraft,
  positional: boolean,
): Pick<TargetingFields, "floor_spec" | "position"> {
  // A non-positional control (IndependentService, VIP, OutOfService …) acts on
  // the whole car and reads no floor. Its floor SET must be cleared: the
  // dispatcher fans out one command per resolved floor, so a spec left behind
  // from a previously chosen car-call command would send the same "go to
  // independent service" order once per floor in the range.
  //
  // `position` is deliberately LEFT AS IT IS. It is not read by these commands,
  // and LiftCloud's dispatcher gates every target on the bank actually having
  // that floor (`bankHasFloor`), so rewriting it to 0 on a bank whose floors
  // start at 1 would turn a working action into one that is silently dropped.
  // Normalising a field the operator can no longer see is not worth that.
  if (!positional) return { position: initial.position, floor_spec: undefined };

  const text = raw.trim();
  // An EMPTY field means no floor, and must be stored as one. Preserving the
  // previous scalar here was defensible while the field was free text, where
  // clearing it was a rare deliberate act; the grid makes emptying the set one
  // click, and the editor says so on screen ("No floor selected — the action
  // targets no particular floor"). Keeping the old position made that sentence a
  // lie: unticking the last box left `position` as it was, `floor_spec` empty,
  // and the server's fallback firing the command at the old floor forever, with
  // no way to reach "no floor" from the grid at all.
  //
  // 0 is the schema's documented unused value, and `floorsField` renders it back
  // as an empty field, so the round trip is closed.
  if (text === "") return { position: 0, floor_spec: undefined };

  const parsed = parseFloorSpec(text);
  if (!parsed.ok) return { position: initial.position, floor_spec: undefined };

  // floor_spec is always assigned, explicitly undefined for the scalar case.
  // formValuesToDraft spreads `...initial` BEFORE these fields, so merely
  // omitting the key would leave a previously stored spec in place — and since
  // the spec wins over position on the server, narrowing a range down to a
  // single floor would appear to save while the action kept firing at the old
  // range.
  if (parsed.floors.length === 1 && STRICT_INTEGER_TEXT.test(text)) {
    return { position: parsed.floors[0], floor_spec: undefined };
  }

  return { floor_spec: text, position: parsed.floors[0] };
}

/**
 * Resolve the form's target selections into the draft's scalar + set fields.
 *
 * A selection of exactly one bank (and likewise one lift) is written as the
 * SCALAR alone, with no set — so an action that targets one place stays
 * byte-identical to how it was stored before granular targeting existed, and
 * upgrading the UI does not silently rewrite every existing row.
 *
 * `bank_id` is always the first selected bank, which is the invariant the server
 * enforces: it is the row's owning bank, and the set must agree with it.
 */
function resolveTargeting(values: FormValues, initial: TimedActionDraft): TargetingFields {
  const banks = values.bank_ids ?? [];
  const lifts = values.lift_ids ?? [];

  // EVERY key is assigned, explicitly undefined when it does not apply.
  // formValuesToDraft spreads `...initial` before these, so a conditionally
  // omitted key leaves the stored value untouched: narrowing a two-lift action
  // down to one would keep the old lift_ids alongside the new scalar, and the
  // set is what the server fans out over — the edit would appear to save while
  // the action carried on firing at both cars.
  return {
    bank_id: banks[0] ?? initial.bank_id,
    lift_id: lifts.length === 1 ? lifts[0] : undefined,
    bank_ids: banks.length > 1 ? banks : undefined,
    lift_ids: lifts.length > 1 ? lifts : undefined,
    ...resolvePosition(values, initial),
  };
}

/**
 * The draft's `position` and `floor_spec`, from whichever field the chosen
 * control actually reads.
 *
 * GenericOutput takes its position from the output field and has no floor set.
 * Without this branch it went through `resolveFloors` as a non-positional
 * control and KEPT the stored position — so a schedule switched to it from a
 * car call at floor 5 fired output 5, a number the operator never entered.
 *
 * The 0 fallback is unreachable from the form, which refuses to submit without
 * a valid output number; it is here so that no path can keep a stale position,
 * and VTS rejects 0 rather than switching an output nobody chose.
 *
 * Every other control goes through `resolveFloors`, gated on the floor table.
 */
function resolvePosition(
  values: FormValues,
  initial: TimedActionDraft,
): Pick<TargetingFields, "floor_spec" | "position"> {
  if (takesOutputNumber(values.control)) {
    const output = values.output_number;
    return { position: isValidOutputNumber(output) ? output : 0, floor_spec: undefined };
  }

  return resolveFloors(values.floors ?? "", initial, isFloorBasedControl(values.control));
}

function formValuesToDraft(
  values: FormValues,
  initial: TimedActionDraft,
  controlOptions: ControlOption[],
): TimedActionDraft {
  const tz = values.timezone ?? initial.timezone;
  const mode = values.recurrence_mode;

  // Resolve stop value: explicit stop_control takes priority, then the
  // default complement of the chosen start control, then the initial value.
  const stopValue =
    values.stop_control ??
    controlOptions.find((o) => o.value === values.control)?.defaultStopValue ??
    initial.stop_message_value;

  // seconds-of-day (wall clock, no UTC conversion)
  const startSec = values.start_time
    ? momentToSecondsOfDay(values.start_time)
    : initial.start_seconds_of_day;
  const stopSec = values.stop_time
    ? momentToSecondsOfDay(values.stop_time)
    : initial.stop_seconds_of_day;

  // days-of-week from Checkbox.Group indices back to bitmask
  const dowIndices = values.days_of_week_indices ?? [];
  const dowBools = Array.from({ length: 7 }, (_, i) => dowIndices.includes(i));
  const dowMask = booleansToDaysOfWeek(dowBools);

  // days-of-month bitmask
  const domDays = values.days_of_month_days ?? [];
  let domMask = daysToMonthMask(domDays);
  domMask = withLastDay(domMask, values.month_last_day === true);

  // ISO strings from DatePicker moments. INTERVAL has no "stop" picker — its
  // window end is start + duration (recurring schedules use start/duration/
  // frequency, not start/stop); ONE_SHOT keeps an explicit stop datetime.
  const startAtISO =
    values.start_at != null ? localDateTimeToISO(values.start_at, tz) : initial.start_at;
  let stopAtISO = initial.stop_at;
  if (mode === RecurrenceMode.INTERVAL) {
    if (values.start_at != null) {
      const durationMinutes = values.duration_minutes ?? 60;
      stopAtISO = localDateTimeToISO(values.start_at.clone().add(durationMinutes, "minutes"), tz);
    }
  } else if (values.stop_at != null) {
    stopAtISO = localDateTimeToISO(values.stop_at, tz);
  }

  const targeting = resolveTargeting(values, initial);

  return {
    ...initial,
    name: values.name,
    enabled: values.enabled,
    start_message_value: values.control,
    stop_message_value: stopValue,
    start_message_active: values.start_active,
    stop_message_active: values.stop_active,
    // targeting carries `position` now — the merged floors field is the single
    // source for both it and floor_spec, so there is no separate position input
    // left to spread afterwards (doing so would overwrite what it resolved).
    ...targeting,
    recurrence_mode: mode,
    timezone: tz,
    start_seconds_of_day: startSec,
    stop_seconds_of_day: stopSec,
    // Every seconds-of-day mode supports the overnight window; ONE_SHOT and
    // INTERVAL use absolute datetimes and have no wraparound concept.
    // MONTHLY used to be excluded here — LiftCloud explicitly rejected
    // stop_next_day for MONTHLY — but that restriction is lifted now that
    // the scheduler's monthlyDue actually honors it. MONTHLY_WEEKDAY shares
    // the same seconds-of-day + overnight-window shape as MONTHLY.
    stop_next_day:
      mode === RecurrenceMode.DAILY ||
      mode === RecurrenceMode.WEEKLY ||
      mode === RecurrenceMode.MONTHLY ||
      mode === RecurrenceMode.MONTHLY_WEEKDAY
        ? deriveStopNextDay(startSec, stopSec)
        : false,
    days_of_week: dowMask,
    days_of_month: domMask !== 0 ? domMask : initial.days_of_month,
    week_interval:
      mode === RecurrenceMode.WEEKLY ? (values.week_interval ?? 1) : initial.week_interval,
    interval_seconds:
      mode === RecurrenceMode.INTERVAL
        ? (values.interval_minutes ?? 1) * 60
        : initial.interval_seconds,
    month_week_ordinal:
      mode === RecurrenceMode.MONTHLY_WEEKDAY
        ? (values.month_week_ordinal ?? 1)
        : initial.month_week_ordinal,
    month_week_last:
      mode === RecurrenceMode.MONTHLY_WEEKDAY
        ? values.month_week_last === true
        : initial.month_week_last,
    start_at: startAtISO,
    stop_at: stopAtISO,
    valid_states: values.valid_states ?? initial.valid_states,
  };
}

// ── Component ────────────────────────────────────────────────────────────────

function ScheduledActionForm({
  initial,
  controlOptions,
  selectors = undefined,
  timezoneDefault,
  submitting,
  onSubmit,
  onCancel,
}: ScheduledActionFormProps): React.ReactElement {
  const [t] = useTranslation("global");
  const [form] = Form.useForm<FormValues>();
  const watchedMode = Form.useWatch("recurrence_mode", form) ?? initial.recurrence_mode;
  const watchedControl = Form.useWatch("control", form);
  const watchedFloors = Form.useWatch("floors", form);
  const watchedBankIds = Form.useWatch("bank_ids", form);
  const watchedLiftIds = Form.useWatch("lift_ids", form);

  const selectedBankIds = useMemo(() => watchedBankIds ?? [], [watchedBankIds]);

  /**
   * The cars this action targets. EMPTY means bank-wide, which is not "no cars":
   * the openings then come from the bank-level flags, which already summarise
   * every car in the bank.
   */
  const selectedLiftIds = useMemo(() => watchedLiftIds ?? [], [watchedLiftIds]);

  /**
   * The cars of the selected banks, grouped by bank.
   *
   * Derived from the CURRENT bank selection rather than a fixed list, which is
   * the whole point: selecting a second bank must make that bank's cars
   * reachable, and deselecting it must take them away again.
   */
  const liftGroups = useMemo(() => {
    if (selectors === undefined) return [];

    return selectors.bankOptions
      .filter((bank) => selectedBankIds.includes(bank.id))
      .map((bank) => ({ bank, lifts: selectors.liftsByBank[bank.id] ?? [] }))
      .filter((group) => group.lifts.length > 0);
  }, [selectors, selectedBankIds]);

  /**
   * The lifts a given bank selection offers.
   *
   * Takes the bank list as an ARGUMENT rather than closing over the watched
   * value, so the submit path can ask about the banks in the values it is about
   * to submit. `Form.useWatch` resolves a render late, and a form submitted in
   * that window would otherwise be checked against a selection that is not yet
   * known — silently skipping the prune exactly when it is first needed.
   */
  const liftIdsForBanks = useCallback(
    (bankIds: string[]): Set<string> => {
      if (selectors === undefined) return new Set<string>();

      return new Set(
        bankIds.flatMap((id) => (selectors.liftsByBank[id] ?? []).map((lift) => lift.id)),
      );
    },
    [selectors],
  );

  /** Every lift id currently offered — the set a selection must stay inside. */
  const selectableLiftIds = useMemo(
    () => new Set(liftGroups.flatMap((group) => group.lifts.map((lift) => lift.id))),
    [liftGroups],
  );

  /**
   * The floors of the SELECTED banks, unioned and de-duplicated by position.
   *
   * De-duplication is by position because position is what the model stores: two
   * banks that both have a floor 3 contribute one row, and ticking it targets
   * floor 3 in both. An opening is offered when ANY contributing bank has it —
   * the dispatcher drops a (bank, floor) pair the bank does not have, so the
   * permissive union costs nothing and hiding the opening would make a floor
   * unreachable in the bank that does have it.
   */
  /**
   * The floors of the SELECTED banks, unioned and de-duplicated by position, with
   * each floor's openings resolved against the SELECTED cars.
   *
   * De-duplication is by position because position is what the model stores: two
   * banks that both have a floor 3 contribute one row, and ticking it targets
   * floor 3 in both.
   *
   * Openings come from `lift_floors` when cars are named — the per-car answer —
   * and from the bank-level flags when the action is bank-wide. A bank where one
   * car has a rear door at the lobby and another does not is ordinary, and the
   * bank-level flag says only that ONE of them does.
   *
   * `degenerate` reports the case the grid cannot express: more than one floor,
   * all sharing one `position`. Position is the only floor number the server
   * matches, so such a bank cannot be addressed floor by floor. It is returned as
   * a FLAG rather than as an empty list, because an empty list is
   * indistinguishable from "not loaded yet" — and conflating them is what made the
   * editor fall back to the expression input with no grid, no toggle and no
   * explanation at all.
   */
  const { options: floorOptions, degenerate: floorsDegenerate } = useMemo((): {
    options: BankFloorOpening[];
    degenerate: boolean;
  } => {
    const byBank = selectors?.floorsByBank;
    if (byBank === undefined) return { options: [], degenerate: false };

    const doorsByBank = selectors?.liftDoorsByBank ?? {};
    const merged = new Map<number, BankFloorOpening>();
    let rawCount = 0;

    selectedBankIds.forEach((bankId) => {
      const bankFloors = byBank[bankId] ?? [];
      rawCount += bankFloors.length;

      // Narrow the car list to the cars OF THIS BANK before resolving. A car
      // selected from another bank has no doors here, and counting it would
      // contribute nothing while making the union look answered.
      const bankLiftIds = selectedLiftIds.filter(
        (liftId) => (doorsByBank[bankId] ?? {})[liftId] !== undefined,
      );

      resolveOpeningsForLifts(bankFloors, bankLiftIds, doorsByBank[bankId] ?? {}).forEach(
        (floor) => {
          const existing = merged.get(floor.position);
          if (existing === undefined) {
            merged.set(floor.position, floor);
            return;
          }

          merged.set(floor.position, {
            ...existing,
            front: existing.front || floor.front,
            rear: existing.rear || floor.rear,
            ...(existing.name === undefined && floor.name !== undefined
              ? { name: floor.name }
              : {}),
          });
        },
      );
    });

    const union = Array.from(merged.values()).sort((a, b) => a.position - b.position);

    return { options: union, degenerate: rawCount > 1 && union.length === 1 };
  }, [selectors, selectedBankIds, selectedLiftIds]);

  /**
   * Floor domain: the positions of the union above. An EMPTY domain disables the
   * check, which is what a still-loading bank looks like — validating against an
   * unknown domain would reject every legal spec.
   */
  const floorDomain = useMemo(() => floorOptions.map((floor) => floor.position), [floorOptions]);

  const trimmedFloors = (watchedFloors ?? "").trim();
  const hasFloors = trimmedFloors !== "";

  /**
   * The domain check applies to SET notation only, not to a bare single floor.
   *
   * This preserves each merged input's own contract. The former "Floor range"
   * field checked its spec against the bank's floors; the former "Floor /
   * position" field was a plain number input that checked nothing. Applying the
   * domain check to a bare floor as well would make every existing action whose
   * stored position is not in its bank's floor list unsaveable — an operator
   * opening such a row to rename it would be blocked by a field they never
   * touched. Merging two inputs must not tighten validation on either.
   */
  const domainForInput = STRICT_INTEGER_TEXT.test(trimmedFloors) ? [] : floorDomain;

  const floorSpecResult = hasFloors ? parseFloorSpec(trimmedFloors, domainForInput) : undefined;

  const floorSpecStatus =
    floorSpecResult !== undefined && !floorSpecResult.ok ? "error" : undefined;

  const floorSpecHelp =
    floorSpecResult !== undefined && !floorSpecResult.ok
      ? describeFloorSpecError(floorSpecResult.error, t)
      : undefined;

  // Show what the spec actually expands to. An operator who typed "(1,5)"
  // expecting five floors sees "2–4" and can correct it before saving.
  const floorSpecHint =
    floorSpecResult !== undefined && floorSpecResult.ok
      ? t("scheduledActions.floorSpecResolved", "Targets floors: {{floors}}", {
          floors: describeFloors(floorSpecResult.floors),
        })
      : t(
          "scheduledActions.floorsHelp",
          "A single floor (3), a range ([1,5) is 1-4, (1,5] is 2-5), or a mix: 3, 7, [10,12].",
        );

  // ── Floor input mode: opening grid (default) vs interval expression ────────

  /**
   * Whether the chosen command takes a floor at all.
   *
   * `undefined` — the form has not reported a control yet — is treated as
   * POSITIONAL so the floors field renders on first paint and does not flicker
   * in once the watch resolves. Note the explicit `=== undefined`: Control's
   * first member `CarCallLock` is 0 and IS floor-based, so a falsiness test here
   * would hide the field for the commonest lock command.
   */
  const isPositionalControl =
    watchedControl === undefined ? true : isFloorBasedControl(watchedControl);

  const controlLabel =
    controlOptions.find((option) => option.value === watchedControl)?.label ??
    t("scheduledActions.controlThisCommand", "This command");

  /**
   * Whether the chosen command's position is an output number (GenericOutput),
   * which replaces the floor section with an output field. Unknown until the
   * form reports a control, like `isPositionalControl` above.
   */
  const isOutputControl = watchedControl !== undefined && takesOutputNumber(watchedControl);

  /**
   * A command whose position 0 is a real floor (ShuttleFloor) cannot be saved
   * with no floor: "no particular floor" would store position 0 and add or
   * remove the LOWEST floor. Save is disabled, and the field says why, until a
   * floor is picked.
   */
  const floorRequired = watchedControl !== undefined && controlRequiresFloor(watchedControl);
  const floorMissing = floorRequired && !hasFloors;

  const noFloorText = floorRequired
    ? t(
        "scheduledActions.floorRequired",
        "Pick at least one floor. {{control}} reads position 0 as a floor, not as no floor, so saving none would change floor 0.",
        { control: controlLabel },
      )
    : t(
        "scheduledActions.floorGridNoneSelected",
        "No floor selected — the action targets no particular floor.",
      );

  // The expression input's note: the requirement while a required floor is
  // missing, the format help otherwise.
  const advancedFloorsExtra = floorMissing ? noFloorText : floorSpecHint;

  const outputRange = { first: GENERIC_OUTPUT_FIRST, last: GENERIC_OUTPUT_LAST };

  // No min/max on the input itself: Ant clamps an out-of-range entry on blur,
  // which would save output 32 for a typed 40. The rule refuses it instead.
  const validateOutputNumber = (_rule: unknown, value: unknown): Promise<void> =>
    isValidOutputNumber(value)
      ? Promise.resolve()
      : Promise.reject(
          new Error(
            t(
              "scheduledActions.outputNumberInvalid",
              "Enter the output number, a whole number from {{first}} to {{last}}.",
              outputRange,
            ),
          ),
        );

  const [advancedRequested, setAdvancedRequested] = useState(false);

  /**
   * The positions the grid would show as ticked, or `undefined` when the stored
   * value is something the grid cannot represent.
   *
   * Two ways it cannot: the text is not a valid spec at all, or it names a floor
   * the selected banks do not have. In BOTH cases the editor must fall back to
   * the expression input rather than render an approximation — silently showing
   * a grid that omits a floor the action really targets would let an operator
   * "save what they see" and quietly drop it.
   */
  const gridSelection = useMemo((): number[] | undefined => {
    if (floorOptions.length === 0) return undefined;
    if (trimmedFloors === "") return [];

    const parsed = parseFloorSpec(trimmedFloors, floorDomain);

    return parsed.ok ? parsed.floors : undefined;
  }, [floorOptions, trimmedFloors, floorDomain]);

  const gridUsable = gridSelection !== undefined;

  /**
   * While the bank's floors are still in flight the field shows a placeholder —
   * NOT the expression input.
   *
   * Rendering the expression as the loading state made the editor swap inputs
   * under the operator: it opened in Advanced because no floors were known yet,
   * and flipped to the grid a network round trip later, taking the box someone
   * had already started typing in with it. `floorsLoading` is what separates
   * "not loaded yet" from "this bank has no floors"; without it the two are the
   * same empty map.
   */
  const floorsLoading = selectors?.floorsLoading === true && floorOptions.length === 0;

  /**
   * Why the floor list is missing, in the operator's words.
   *
   * A permission error, a dropped connection and a bank that genuinely has no
   * floors configured all used to read as one sentence, which told nobody what
   * to do next. `no-floors` is the actionable one: the bank has no floor rows,
   * which is fixed in Lifts & Floors rather than by retrying.
   */
  const floorsUnavailableMessage = useMemo((): string => {
    const reasons = selectors?.floorLoadFailures ?? {};
    const kinds = new Set(selectedBankIds.map((id) => reasons[id]).filter(Boolean));

    if (kinds.size === 1 && kinds.has("no-floors")) {
      return t(
        "scheduledActions.floorsNoneConfigured",
        "The selected banks have no floors configured, so there is nothing to pick. Add floors in Lifts & Floors, or name them here directly.",
      );
    }

    return t(
      "scheduledActions.floorsUnavailable",
      "The floor list for the selected banks could not be loaded, so floors have to be named here directly.",
    );
  }, [selectors, selectedBankIds, t]);
  const showAdvancedInput =
    !floorsLoading && (advancedRequested || !gridUsable || floorsDegenerate);

  const handleGridChange = useCallback(
    (positions: number[]): void => {
      form.setFieldValue("floors", floorsToSpec(positions));
    },
    [form],
  );

  // Populate form whenever the initial draft changes (edit scenario).
  useEffect(() => {
    form.setFieldsValue(draftToFormValues(initial, timezoneDefault));
  }, [form, initial, timezoneDefault]);

  /**
   * Drop selected lifts that the current bank selection no longer offers.
   *
   * Without this, deselecting a bank leaves its cars silently selected: the
   * chips vanish from the dropdown's option list but the VALUE keeps them, so
   * the form would submit lifts belonging to a bank the action does not target.
   * The server drops such a lift and logs it, meaning the operator's stated
   * intent and what actually fires would disagree with no visible sign.
   *
   * Only prunes once the offered set is actually KNOWN. On the first commit
   * `Form.useWatch` has not yet reported the bank selection, so the offered set
   * is momentarily empty — pruning against it would treat every selected lift as
   * an orphan and clear the field each time the editor opened. An empty offered
   * set therefore means "not known yet", never "nothing is valid".
   */
  useEffect(() => {
    if (selectors === undefined || selectableLiftIds.size === 0) return;

    const current: string[] = form.getFieldValue("lift_ids") ?? [];
    const kept = current.filter((id) => selectableLiftIds.has(id));
    if (kept.length !== current.length) form.setFieldValue("lift_ids", kept);
  }, [form, selectors, selectableLiftIds]);

  // When the selected control changes, prefill the default stop value — but only
  // when stop is currently empty. Otherwise this effect, firing right after the
  // edit-population effect above, would overwrite a persisted stop_control (edit)
  // or an explicit operator choice with the start control's default complement.
  useEffect(() => {
    if (watchedControl === undefined) return;
    if (form.getFieldValue("stop_control") !== undefined) return;
    const chosen = controlOptions.find((o) => o.value === watchedControl);
    if (chosen?.defaultStopValue !== undefined) {
      form.setFieldValue("stop_control", chosen.defaultStopValue);
    }
  }, [watchedControl, controlOptions, form]);

  const handleFinish = (values: FormValues): void => {
    // Belt and braces: the submit button is disabled while the spec is invalid,
    // but a form can also be submitted by pressing Enter, and the one thing this
    // field must never do is let a malformed spec reach the server.
    //
    // Gated on the control being positional, matching the button. A non-positional
    // command discards the floor set entirely (see resolveFloors), so a stale
    // malformed spec left in the hidden field must not block a save the operator
    // has no field on screen to fix.
    const spec = (values.floors ?? "").trim();
    const domain = STRICT_INTEGER_TEXT.test(spec) ? [] : floorDomain;
    if (isFloorBasedControl(values.control) && spec !== "" && !parseFloorSpec(spec, domain).ok) {
      return;
    }

    // The same belt and braces for the two position rules the button enforces:
    // ShuttleFloor with no floor would store position 0, its lowest floor, and a
    // GenericOutput without a valid output number has nothing VTS will accept.
    if (controlRequiresFloor(values.control) && spec === "") return;
    if (takesOutputNumber(values.control) && !isValidOutputNumber(values.output_number)) return;

    // Prune orphan lifts HERE as well as in the effect above. The effect cannot
    // be the only guard: it runs a render after the bank selection is observed,
    // so a form submitted in that window would still carry a lift belonging to
    // no targeted bank. This path is the authoritative one.
    const offered = liftIdsForBanks(values.bank_ids ?? []);
    const pruned: FormValues =
      offered.size === 0
        ? values
        : { ...values, lift_ids: (values.lift_ids ?? []).filter((id) => offered.has(id)) };

    const draft = formValuesToDraft(pruned, initial, controlOptions);
    onSubmit(draft);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={draftToFormValues(initial, timezoneDefault)}
    >
      {/* Name */}
      <Form.Item
        name="name"
        label={t("scheduledActions.name", "Name")}
        rules={[
          { required: true, message: t("scheduledActions.nameRequired", "Name is required") },
        ]}
      >
        <Input />
      </Form.Item>

      {/* Control (start message) */}
      <Form.Item
        name="control"
        label={t("scheduledActions.control", "Control")}
        rules={[
          { required: true, message: t("scheduledActions.controlRequired", "Control is required") },
        ]}
      >
        <Select style={{ width: "100%" }}>
          {controlOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* Start command's active state — carried on UiCmd.active at dispatch,
          not a distinct message. Default on: unchecking it does not change
          WHICH command fires, only whether it dispatches as active or not. */}
      <Form.Item
        name="start_active"
        label={t("scheduledActions.startActive", "Start command active")}
        valuePropName="checked"
        tooltip={t(
          "scheduledActions.startActiveTooltip",
          "Whether the start command fires as active (on) or inactive (off). The same command can be used for both start and stop as long as one is active and the other is not.",
        )}
      >
        <Switch />
      </Form.Item>

      {/* Stop command (optional) */}
      <Form.Item
        name="stop_control"
        label={t("scheduledActions.stopControl", "Stop command")}
        dependencies={["control", "start_active", "stop_active"]}
        rules={[
          ({ getFieldValue }) => ({
            validator: async (_: unknown, value: number | undefined) => {
              const startControl = getFieldValue("control") as number | undefined;
              const startActive = getFieldValue("start_active") as boolean | undefined;
              const stopActive = getFieldValue("stop_active") as boolean | undefined;
              const stopControl = value ?? startControl;
              if (stopControl === startControl && (stopActive ?? true) === (startActive ?? true)) {
                return Promise.reject(
                  new Error(
                    t(
                      "scheduledActions.startStopIdentical",
                      "Start and stop must differ in command, active state, or both",
                    ),
                  ),
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <Select style={{ width: "100%" }} allowClear>
          {controlOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* Stop command's active state — independent of the start side. Same
          command, opposite active states is the common "one message, two
          states" configuration (e.g. CarCallLock active:true to start, the
          SAME CarCallLock active:false to stop). */}
      <Form.Item
        name="stop_active"
        label={t("scheduledActions.stopActive", "Stop command active")}
        valuePropName="checked"
        dependencies={["control", "stop_control", "start_active"]}
        tooltip={t(
          "scheduledActions.stopActiveTooltip",
          'Whether the stop command fires as active (on) or inactive (off). Set this to off with the same command as Start to express "lock on, then release" as one message instead of two.',
        )}
      >
        <Switch />
      </Form.Item>

      {/* Bank selector — multiple; the first is the owning bank */}
      {selectors !== undefined && selectors.bankOptions.length > 0 && (
        <Form.Item
          name="bank_ids"
          label={t("scheduledActions.banks", "Banks")}
          extra={t(
            "scheduledActions.banksHelp",
            "Select one or more banks. The action fires at every selected bank.",
          )}
          rules={[
            {
              required: true,
              message: t("scheduledActions.bankRequired", "At least one bank is required"),
            },
          ]}
        >
          <Select mode="multiple" style={{ width: "100%" }} optionFilterProp="children">
            {selectors.bankOptions.map((b) => (
              <Option key={b.id} value={b.id}>
                {b.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {/*
        Lift selector — the cars of the SELECTED banks, grouped by bank so the
        "Car 1" of one bank is distinguishable from the "Car 1" of another.
        Empty means every lift in every selected bank.
      */}
      {selectors !== undefined && liftGroups.length > 0 && (
        <Form.Item
          name="lift_ids"
          label={t("scheduledActions.lifts", "Lifts")}
          extra={t(
            "scheduledActions.liftsHelp",
            "Leave empty to target every lift in the selected banks. Pick one or more to narrow it. Only lifts of the selected banks are listed.",
          )}
        >
          <Select mode="multiple" style={{ width: "100%" }} allowClear optionFilterProp="label">
            {liftGroups.map((group) => (
              <Select.OptGroup key={group.bank.id} label={group.bank.name}>
                {group.lifts.map((lift) => (
                  <Option key={lift.id} value={lift.id} label={`${group.bank.name} ${lift.name}`}>
                    {lift.name}
                  </Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
        </Form.Item>
      )}

      {/*
        The lift picker above disappears with no explanation when none of the
        selected banks contributed a car — indistinguishable, to an operator,
        from "this feature doesn't apply here". `floorsLoading` is reused
        rather than a new lifts-specific flag: floors and lifts arrive on the
        SAME per-bank response, so one "still fetching" signal already covers
        both. Gated on a bank actually being selected, matching the floors
        alert below, so an editor that has simply not reported its bank
        selection yet does not flash this before the picker has a chance to
        appear.
      */}
      {selectors !== undefined &&
        liftGroups.length === 0 &&
        selectors.floorsLoading !== true &&
        selectedBankIds.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            data-testid="lifts-unavailable-alert"
            message={t(
              "scheduledActions.liftsNoneConfigured",
              "The selected banks have no individual lifts on record, so this action targets the whole bank. If a bank does have cars, add them in Lifts & Floors to pick specific ones here.",
            )}
          />
        )}

      {/*
        Floors.

        Two ways to name the same set. The DEFAULT is a grid of the bank's real
        floors with a checkbox per opening; the interval expression that used to
        be the only input now sits behind "Advanced". The expression is not a
        legacy fallback — it is the only way to say "every floor from 10 up" on a
        sixty-storey bank without sixty clicks — but it is expert notation, and
        making it the front door meant an operator had to already know the bank's
        floor positions and that `[1,5)` excludes 5.

        The whole section disappears for a command that has no floor. That is not
        cosmetic: `IndependentService` puts a CAR into a service mode, and the
        dispatcher fans out one command per resolved floor, so a floor set left
        over from a previously chosen car-call command would send the same order
        once per floor.
      */}
      {isPositionalControl && (
        <>
          {/*
            The mode toggle sits OUTSIDE the Form.Item, not inside its label. A
            button nested in a <label> is bound to the labelled input: clicking it
            also activates the field, and every by-label query finds two controls
            where there is one.

            Hidden — not disabled — while the grid cannot represent the current
            value. A disabled toggle offers a way back to a list that genuinely
            cannot show what is stored; the alert below says why instead, and the
            toggle returns as soon as it could work.
          */}
          {gridUsable && !floorsLoading && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto" }}
                onClick={() => setAdvancedRequested((on) => !on)}
                data-testid="floors-advanced-toggle"
              >
                {showAdvancedInput
                  ? t("scheduledActions.floorsUseGrid", "Use floor list")
                  : t("scheduledActions.floorsAdvanced", "Advanced")}
              </Button>
            </div>
          )}

          <Form.Item
            name="floors"
            label={t("scheduledActions.floors", "Floors")}
            extra={showAdvancedInput ? advancedFloorsExtra : undefined}
            validateStatus={floorSpecStatus}
            help={floorSpecHelp}
            // The expression input is the field's real control. In grid mode it
            // is hidden rather than unmounted, so the grid keeps writing to the
            // SAME form value and switching modes never loses a selection.
            style={showAdvancedInput ? undefined : { marginBottom: 8 }}
          >
            <Input
              placeholder="3   ·   [1,5)   ·   3, 7, [10,12]"
              allowClear
              style={showAdvancedInput ? undefined : { display: "none" }}
            />
          </Form.Item>

          {floorsLoading && (
            <Form.Item>
              <Skeleton active paragraph={{ rows: 3 }} title={false} />
              <Typography.Text type="secondary">
                {t("scheduledActions.floorGridLoading", "Loading the bank's floors…")}
              </Typography.Text>
            </Form.Item>
          )}

          {!showAdvancedInput && !floorsLoading && (
            <Form.Item>
              <FloorOpeningPicker
                floors={floorOptions}
                control={watchedControl}
                value={gridSelection ?? []}
                onChange={handleGridChange}
                // Frozen while the save is in flight, so a tick landing after
                // the draft was handed off cannot leave the screen showing a
                // selection the server was never told about.
                disabled={submitting}
              />
              <Typography.Text type={floorMissing ? "danger" : "secondary"}>
                {hasFloors ? floorSpecHint : noFloorText}
              </Typography.Text>
            </Form.Item>
          )}

          {floorsDegenerate && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              data-testid="floors-degenerate-alert"
              message={t(
                "scheduledActions.floorsDegenerate",
                "This bank's floors all share one stored position, so they cannot be picked individually. Set distinct floor positions in Lifts & Floors, or name the floors here directly.",
              )}
            />
          )}

          {/*
            Only when a bank is actually SELECTED. With none selected there are
            no floors by definition, and `Form.useWatch` reports the selection a
            render late — so an editor that had simply not reported its banks yet
            announced that the floor list "could not be loaded", which was not
            true and sent people looking for a fault that did not exist.
          */}
          {!floorsDegenerate &&
            !floorsLoading &&
            floorOptions.length === 0 &&
            selectedBankIds.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                data-testid="floors-unavailable-alert"
                message={floorsUnavailableMessage}
              />
            )}

          {showAdvancedInput && !gridUsable && !floorsDegenerate && floorOptions.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(
                "scheduledActions.floorsAdvancedForced",
                "This floor range cannot be shown as a list — clear it to pick floors from the bank instead.",
              )}
            />
          )}
        </>
      )}

      {/*
        Output number, in place of the floor section, for GenericOutput: its
        position is the controller's generic output (1-32), which VTS sends as
        C1027's OutputId. Empty unless the stored action already was a
        GenericOutput — a position carried over from a car call is a floor, and
        presetting it here is how a floor-5 car call became output 5.
      */}
      {isOutputControl && (
        <Form.Item
          name="output_number"
          label={t("scheduledActions.outputNumber", "Output number")}
          extra={t(
            "scheduledActions.outputNumberHelp",
            "The controller's generic output this command switches, {{first}}-{{last}}. It is sent as the command's position, so it replaces the floor.",
            outputRange,
          )}
          rules={[{ validator: validateOutputNumber }]}
        >
          <InputNumber
            placeholder={`${GENERIC_OUTPUT_FIRST}-${GENERIC_OUTPUT_LAST}`}
            style={{ width: "100%" }}
            data-testid="output-number-input"
          />
        </Form.Item>
      )}

      {!isPositionalControl && !isOutputControl && (
        <Form.Item label={t("scheduledActions.floors", "Floors")}>
          <Alert
            type="info"
            showIcon
            message={t(
              "scheduledActions.floorsNotApplicable",
              "{{control}} applies to the whole car and takes no floor.",
              { control: controlLabel },
            )}
          />
        </Form.Item>
      )}

      {/* Enabled */}
      <Form.Item
        name="enabled"
        label={t("scheduledActions.enabled", "Enabled")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      {/* Recurrence mode */}
      <Form.Item
        name="recurrence_mode"
        label={t("scheduledActions.recurrenceMode", "Recurrence mode")}
        rules={[{ required: true }]}
      >
        <Select style={{ width: "100%" }}>
          {/*
           * Object.keys returns the enum's NUMERIC keys as strings ("0".."4");
           * map them back to numbers so each Option value is the numeric
           * RecurrenceMode. Using the raw string keys made the stored value a
           * string, which silently failed every `mode === RecurrenceMode.X`
           * numeric comparison — breaking the mode-specific field rendering and
           * the interval_seconds/week_interval/days_of_month draft conversion.
           */}
          {(Object.keys(RECURRENCE_LABELS).map(Number) as RecurrenceMode[]).map((rm) => (
            <Option key={rm} value={rm}>
              {t(
                // Strips everything but letters/digits, not just "-", so a
                // label with spaces or parentheses (e.g. "Monthly (weekday)")
                // still resolves to a clean translation key.
                `scheduledActions.mode.${RECURRENCE_LABELS[rm].toLowerCase().replace(/[^a-z0-9]/g, "")}`,
                RECURRENCE_LABELS[rm],
              )}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* Mode-specific fields */}
      <ScheduledActionModeFields mode={watchedMode} />

      {/* Valid states — optional tag input */}
      <Form.Item name="valid_states" label={t("scheduledActions.validStates", "Valid states")}>
        <Select mode="tags" style={{ width: "100%" }} tokenSeparators={[","]} />
      </Form.Item>

      {/* Actions */}
      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            // A floor-spec error only blocks the save when the chosen command
            // actually uses floors. Otherwise the spec is discarded on submit and
            // there is no visible field for the operator to correct. A missing
            // required floor (ShuttleFloor) blocks it too, and says so beside
            // the field; a missing output number is the output field's own rule.
            disabled={(isPositionalControl && floorSpecStatus === "error") || floorMissing}
          >
            {t("general.save", "Save")}
          </Button>
          <Button onClick={onCancel}>{t("general.cancel", "Cancel")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

export default ScheduledActionForm;
